// src/components/ElementScopePage1ReadOnly.jsx
// Read-only view of ElementScopePage1.jsx (the "Construction / Scope" step),
// opened from the Estimation Summary's step tabs so a finished project's
// data can be reviewed without risking an accidental edit. All fields are
// wrapped in a disabled <fieldset>, and there are no Save/Skip/Finish
// actions -- only navigation back to Summary or across to another step's
// own read-only view. The real ElementScopePage1.jsx is untouched.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api as axios, authFetch } from "../config/apiConfig";
import { API_ORIGIN } from "../config/apiConfig";
import useAutoResizeTextareas from "../utils/useAutoResizeTextareas";
import { ROUTES } from "../config/routes";
import { printProjectReport } from "../utils/printProjectReport";
import RichTextEditor from "./RichTextEditor";

// FIX: was hardcoded to "/api" -- see the same fix in
// Step1ProjectInfo.jsx; every other page derives this from API_ORIGIN.
const API_BASE = API_ORIGIN;

// Same wizard-step indicator as every other Cost Estimation page.
const WIZARD_STEPS = [
  { key: "project", icon: "ti-file-description", label: "Project", sub: "Info & Overview" },
  { key: "budget", icon: "ti-currency-riyal", label: "Budget", sub: "Amount & Summary" },
  { key: "asset", icon: "ti-building", label: "Asset", sub: "Selection & Element" },
  { key: "element", icon: "ti-list-check", label: "Element", sub: "Main & Sub Element" },
  { key: "estimation", icon: "ti-report-money", label: "Estimation", sub: "Amount & Summary" },
  { key: "basis", icon: "ti-clipboard-text", label: "Basis of Cost", sub: "Assumption & Exclusion" },
  { key: "construction", icon: "ti-hammer", label: "Construction", sub: "Scope" },
];

// Which of the 7 wizard steps this PAGE is. Steps before this one get
// the theme's own light-green "done" tint (.wizard-item-done -- built
// into app.min.css, so this part always rendered correctly); THIS step
// gets the solid brand-green "current" highlight (.wizard-item-current,
// defined in src/styles/themeOverrides.css -- see Layout.jsx for why
// that file, not appLayout.css, is what actually loads it); steps after
// this one stay inert (.disabled).
const CURRENT_STEP_KEY = "construction";
const CURRENT_STEP_INDEX = WIZARD_STEPS.findIndex((s) => s.key === CURRENT_STEP_KEY);

// Read-only step viewer: clicking any wizard tab from this page (or from
// the Estimation Summary's step tiles) goes to that step's own read-only
// view route, never the real editable page.
const STEP_VIEW_ROUTES = {
  project: ROUTES.COST_ESTIMATION_STEP1_VIEW,
  budget: ROUTES.COST_ESTIMATION_STEP2_VIEW,
  asset: ROUTES.COST_ESTIMATION_STEP3_VIEW,
  element: ROUTES.COST_ESTIMATION_STEP4_VIEW,
  estimation: ROUTES.COST_ESTIMATION_STEP5_VIEW,
  basis: ROUTES.BASIS_OF_COST_VIEW,
  construction: ROUTES.ELEMENT_SCOPE_PAGE1_VIEW,
};

export default function ElementScopePage1ReadOnly() {
  useAutoResizeTextareas();
  const navigate = useNavigate();
  const [projectId] = useState(localStorage.getItem("projectID") || "");
  // Same "Project : <name>" white-block header as the rest of the wizard
  // -- see the matching comment in BasisOfCost.jsx.
  const [projectName] = useState(localStorage.getItem("projectName") || "");

  // Part 1 fields (from ElementScopePage1)
  const [part1, setPart1] = useState({
    FacilitatingWorks: "",
    Substructure: "",
    Superstructure: "",
    InternalFinishes: "",
    FittingsFurnishingsEquipment: "",
    Services: "",
    CompletedBuildingsUnits: "",
    WorksToExistingBuildings: "",
    ExternalWorks: ""
  });

  // Part 2 fields (from ElementScopePage2)
  const [part2, setPart2] = useState({
    MainContractorPreliminaries: "",
    ProjectDesignTeamFees: "",
    RiskAllowances: "",
    VATAssessment: "",
    MainContractorOverheadsProfit: "",
    OtherDevelopmentCosts: "",
    Inflation: ""
  });

  // FIX: Part 1's fields used to be behind their own collapse toggle, but
  // the theme's Construction Scope tab shows them directly (no toggle) --
  // only "Other Scope" (Part 2) is the theme's collapsible accordion. So
  // Part 1 no longer needs an open/closed state; part2Open still drives
  // that accordion below (via plain React state, not data-bs-toggle --
  // this app doesn't load Bootstrap's JS, see Header.jsx's comment on the
  // dropdown-positioning bug).
  // Read-only view: start this section expanded so its data is visible
  // without needing to interact with the (now-disabled) toggle button.
  const [part2Open, setPart2Open] = useState(true);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    authFetch(`${API_BASE}/element_scope?Projectid=${encodeURIComponent(projectId)}`)
      .then(r => r.json())
      .then(j => {
        if (j.success && j.data) {
          const d = j.data;
          setPart1({
            FacilitatingWorks: d.FacilitatingWorks || "",
            Substructure: d.Substructure || "",
            Superstructure: d.Superstructure || "",
            InternalFinishes: d.InternalFinishes || "",
            FittingsFurnishingsEquipment: d.FittingsFurnishingsEquipment || "",
            Services: d.Services || "",
            CompletedBuildingsUnits: d.CompletedBuildingsUnits || "",
            WorksToExistingBuildings: d.WorksToExistingBuildings || "",
            ExternalWorks: d.ExternalWorks || ""
          });
          setPart2({
            MainContractorPreliminaries: d.MainContractorPreliminaries || "",
            ProjectDesignTeamFees: d.ProjectDesignTeamFees || "",
            RiskAllowances: d.RiskAllowances || "",
            VATAssessment: d.VATAssessment || "",
            MainContractorOverheadsProfit: d.MainContractorOverheadsProfit || "",
            OtherDevelopmentCosts: d.OtherDevelopmentCosts || "",
            Inflation: d.Inflation || ""
          });
        }
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [projectId]);

  const handlePart1Change = (k, v) => setPart1(prev => ({ ...prev, [k]: v }));
  const handlePart2Change = (k, v) => setPart2(prev => ({ ...prev, [k]: v }));

  // Single combined save — both pages used to write to the same
  // /element_scope row separately; now it's one request with every field.
  const save = async () => {
    if (!projectId) {
      setError("Missing projectID");
      return false;
    }

    setSaving(true);
    setError(null);

    const payload = { Projectid: projectId, ...part1, ...part2 };

    try {
      const res = await authFetch(`${API_BASE}/element_scope`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const j = await res.json();

      if (j.success) {
        setSuccessMsg(j.message || "Saved successfully");
        setTimeout(() => setSuccessMsg(null), 3500);
        return true;
      }

      setError(j.error || "Save failed");
      return false;
    } catch (e) {
      setError(String(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = async () => {
    const ok = await save();
    if (!ok) return;

    const pid = localStorage.getItem("projectID");
    if (!pid) {
      alert("Project ID missing.");
      return;
    }

    try {
      await axios.put(`${API_BASE}/projects/status`, {
        projectid: pid,
        status: "C"
      });

      navigate(ROUTES.COST_ESTIMATION_SUMMARY);
    } catch (err) {
      console.error("Status update failed:", err);
      alert("Failed to update project status.");
    }
  };

  // Same 9 Part-1 fields / 7 Part-2 fields as before, just split into the
  // theme's exact left/right column layout (cost-estimation.html's
  // #ceElementScope pane) instead of one flat list per part.
  const part1LeftFields = [
    { key: "FacilitatingWorks", label: "Facilitating Works" },
    { key: "Superstructure", label: "Superstructure" },
    { key: "FittingsFurnishingsEquipment", label: "Fittings, Furnishings & Equipment" },
    { key: "CompletedBuildingsUnits", label: "Completed Buildings & Building Units" },
    { key: "ExternalWorks", label: "External Works" }
  ];
  const part1RightFields = [
    { key: "Substructure", label: "Substructure" },
    { key: "InternalFinishes", label: "Internal Finishes" },
    { key: "Services", label: "Services" },
    { key: "WorksToExistingBuildings", label: "Works to Existing Buildings" }
  ];

  const part2LeftFields = [
    { key: "MainContractorPreliminaries", label: "Main Contractor's Preliminaries" },
    { key: "ProjectDesignTeamFees", label: "Project / Design Team Fees" },
    { key: "RiskAllowances", label: "Risk Allowances" },
    { key: "VATAssessment", label: "VAT Assessment" }
  ];
  const part2RightFields = [
    { key: "MainContractorOverheadsProfit", label: "Main Contractor's Overheads & Profit" },
    { key: "OtherDevelopmentCosts", label: "Other Development Costs" },
    { key: "Inflation", label: "Inflation" }
  ];

  // Per request: once a project has been through the full wizard and
  // Finish has been clicked, every read-only step page for it gets a
  // "Print Report" / "View Dashboard" shortcut in the top-right corner,
  // below the breadcrumb -- no need to jump back to the Estimation
  // Summary page just to print the report or check the portfolio
  // Dashboard. Print Report reuses printProjectReport.js's self-contained
  // generator (the same one ProjectList.jsx's "Estimated" column icon
  // already calls) -- given just a projectId, it fetches everything
  // itself and opens the report exactly the same way.
  const [printReportLoading, setPrintReportLoading] = useState(false);
  const handlePrintReport = async () => {
    const projectId = localStorage.getItem("projectID");
    if (!projectId) return;
    setPrintReportLoading(true);
    try {
      await printProjectReport({ authFetch, API_BASE, projectId });
    } catch (err) {
      console.error("Failed to generate report", err);
    } finally {
      setPrintReportLoading(false);
    }
  };

  return (
    <>
      {/* Was a plain "Start Cost Estimation" title -- replaced with the
          actual project name in a white block, matching every other step
          in this wizard. */}
      <div className="page-title-head d-flex align-items-center flex-wrap gap-2">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail instead of a
              plain bordered/uppercase title: "Cost Estimation /
              Construction / Project : {name}", each non-current segment
              (and the "/" separators) in #788ca5 at 14px, normal case,
              no underline -- matching the reference design. The current
              segment ("Project : {name}") stays semibold in the app's
              brand teal so it still reads as the active/current item,
              same idea as the reference's blue "Portfolio" segment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Project</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              {projectName || "—"}
            </span>
          </nav>
        </div>

        {/* Per request: "Print Report" / "View Dashboard" shortcut, top
            right corner below the header, same on every read-only wizard
            step page for a finished project. */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm text-white px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#124d52", borderRadius: 2 }}
            onClick={handlePrintReport}
            disabled={printReportLoading}
          >
            <i className="ti ti-printer fs-5" />
            {printReportLoading ? "Preparing..." : "Print Report"}
          </button>
          <button
            type="button"
            className="btn btn-sm px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#02bc9c", color: "#fff", borderRadius: 2 }}
            onClick={() => navigate(ROUTES.DASHBOARD)}
          >
            <i className="ti ti-dashboard fs-5" />
            View Dashboard
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {/* Wizard progress bar -- see the note above WIZARD_STEPS in
                  Step1ProjectInfo.jsx for why this is a fixed percentage
                  per page instead of the theme's JS-driven one. */}
              <div className="progress mb-4" style={{ height: 6 }}>
                <div className="progress-bar wizard-progress-bar" style={{ width: "100%" }} />
              </div>


              {/* Step indicator -- see the note above WIZARD_STEPS. On this
                  read-only view every step is treated as "done" (a saved
                  project has no "current" step) and every tab is clickable,
                  jumping straight to that step's own read-only view. */}
              <ul className="nav nav-tabs wizard-tabs w-100 row g-2 border-0" role="tablist">
                {WIZARD_STEPS.map((step, idx) => (
                  <li className="nav-item col-12 col-lg" key={step.key}>
                    <span
                      className="nav-link h-100 wizard-item-done"
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(STEP_VIEW_ROUTES[step.key])}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(STEP_VIEW_ROUTES[step.key]);
                        }
                      }}
                    >
                      <span className="d-flex align-items-center">
                        {step.key === "budget" ? (
                          <svg
                            className="flex-shrink-0"
                            style={{ width: "32px", height: "32px" }}
                            viewBox="0 0 917 1025"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" d="M 916 834 L 570 908 L 550 964 L 542 1001 L 540 1024 L 884 951 L 893 932 L 904 899 L 912 865 Z M 646 54 L 539 144 L 539 473 L 435 496 L 430 1 L 324 89 L 324 518 L 83 570 L 51 682 L 323 629 L 324 767 L 32 830 L 0 946 L 310 881 L 346 863 L 428 746 L 437 604 L 539 584 L 539 803 L 885 730 L 916 614 L 646 670 L 646 561 L 885 509 L 916 397 L 646 449 Z" />
                          </svg>
                        ) : (
                          <i className={`ti ${step.icon} fs-32 flex-shrink-0`} />
                        )}
                        <span className="flex-grow-1 ms-2 text-truncate">
                          <span className="mb-0 lh-base d-block fw-semibold text-body fs-base">{step.label}</span>
                          <span className="fs-xxs mb-0">{step.sub}</span>
                        </span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <hr className="mt-3" />

              <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

              <h5 className="mb-3 text-green fs-4 fw-bold">Element Construction Scope</h5>
              <hr className="mt-0 mb-3" />

              {loading && <div className="text-muted mb-2">Loading...</div>}
              {error && <div className="alert alert-danger">{error}</div>}
              {successMsg && <div className="alert alert-success">{successMsg}</div>}

              <div className="row">
                <div className="col-xl-6">
                  {part1LeftFields.map(field => (
                    <div key={field.key} className="mb-3">
                      <label className="form-label">{field.label}</label>
                      <RichTextEditor
                        name={field.key}
                        value={part1[field.key]}
                        onChange={e => handlePart1Change(field.key, e.target.value)}
                        disabled
                      />
                    </div>
                  ))}
                </div>
                <div className="col-xl-6">
                  {part1RightFields.map(field => (
                    <div key={field.key} className="mb-3">
                      <label className="form-label">{field.label}</label>
                      <RichTextEditor
                        name={field.key}
                        value={part1[field.key]}
                        onChange={e => handlePart1Change(field.key, e.target.value)}
                        disabled
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Other Scope -- theme's collapsible accordion (Prelims,
                  Risk, Inflation, Contingency, VAT etc.). Driven by
                  part2Open/React state rather than data-bs-toggle since
                  this app doesn't load Bootstrap's JS (see Header.jsx's
                  dropdown-positioning comment). */}
              <div className="accordion my-3">
                <div className="accordion-item border">
                  <h2 className="accordion-header">
                    <button
                      type="button"
                      className={`accordion-button bg-disabled fw-semibold${part2Open ? "" : " collapsed"}`}
                      onClick={() => setPart2Open(o => !o)}
                    >
                      Other Scope (Prelims, Risk, Inflation, Contingency, VAT etc.)
                    </button>
                  </h2>
                  {part2Open && (
                    <div className="accordion-collapse collapse show">
                      <div className="accordion-body">
                        <div className="row">
                          <div className="col-xl-6">
                            {part2LeftFields.map(field => (
                              <div key={field.key} className="mb-3">
                                <label className="form-label">{field.label}</label>
                                <RichTextEditor
                                  name={field.key}
                                  value={part2[field.key]}
                                  onChange={e => handlePart2Change(field.key, e.target.value)}
                                  disabled
                                />
                              </div>
                            ))}
                          </div>
                          <div className="col-xl-6">
                            {part2RightFields.map(field => (
                              <div key={field.key} className="mb-3">
                                <label className="form-label">{field.label}</label>
                                <RichTextEditor
                                  name={field.key}
                                  value={part2[field.key]}
                                  onChange={e => handlePart2Change(field.key, e.target.value)}
                                  disabled
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              </fieldset>

              <div className="d-flex justify-content-between mt-4">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.DASHBOARD, { state: { tab: "executive" } })}>
                  &larr; Back to Dashboard
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.COST_ESTIMATION_SUMMARY)}>
                  &larr; Back to Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}